import axios from 'axios';
import { endpoint } from '../config';

const api = axios.create({
  baseURL: endpoint,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});

// --- Auth ---

export async function checkAuth() {
  const response = await fetch(`${endpoint}/api/user`, {
    credentials: 'include',
    headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
  });
  if (response.ok) return response.json();
  if (response.status === 401) return null;
  throw new Error('Failed to fetch user data');
}

export async function logout() {
  const response = await fetch(`${endpoint}/auth/logout`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!response.ok) throw new Error('Logout failed');
}

export async function disconnect() {
  const response = await fetch(`${endpoint}/auth/disconnect`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!response.ok) throw new Error('Disconnect failed');
}

export function getLoginUrl(parentUUID) {
  return `${endpoint}/auth/google/login?parent=${parentUUID || ''}`;
}

// --- Tasks ---

export async function getTask(id) {
  const res = await api.post('/api/getTask', { id });
  return res.data;
}

export async function getChildTasks(parent) {
  const res = await api.post('/api/getChildTasks', { parent });
  return res.data.Tasks || [];
}

export async function getOwnedTasks() {
  const res = await api.post('/api/getOwnedTasks');
  return res.data;
}

export async function createParentTask(taskData) {
  const res = await api.post('/api/createParentTask', taskData);
  return res.data;
}

export async function createChildTask(parent, label, content) {
  const res = await api.post('/api/createChildTask', { parent, label: label.trim(), content });
  return res.data;
}

export async function updateTaskStatus(id, status) {
  const res = await api.post('/api/updateTaskStatus', { id, status });
  return res.data;
}

export async function updateTask(taskData) {
  const res = await api.post('/api/updateTask', taskData);
  return res.data;
}

export async function deleteTask(id) {
  const res = await api.post('/api/deleteTask', { id });
  return res.data;
}

// --- Delegates ---

export async function getDelegates() {
  const res = await api.get('/api/delegates');
  return res.data;
}

export async function createDelegate({ name, email, mobile }) {
  const res = await api.post('/api/delegates', { name, email, mobile });
  return res.data;
}

export async function updateDelegate(uuid, data) {
  const res = await api.put(`/api/delegates/${uuid}`, data);
  return res.data;
}

export async function deleteDelegate(uuid) {
  const res = await api.delete(`/api/delegates/${uuid}`);
  return res.data;
}

export async function getTaskDelegates(taskUuid) {
  const res = await api.get(`/api/tasks/${taskUuid}/delegates`);
  return res.data;
}

export async function setTaskDelegates(taskUuid, delegateUuids) {
  const res = await api.put(`/api/tasks/${taskUuid}/delegates`, { delegateUuids });
  return res.data;
}

export async function reorderTasks(parent, order) {
  const res = await api.post('/api/reorderTasks', { parent, order });
  return res.data;
}

// --- Templates ---

export async function getTemplateTasks() {
  const res = await api.post('/api/getTemplateTasks');
  return res.data;
}

export async function cloneTemplate(templateUuid) {
  const res = await api.post('/api/cloneTemplate', { templateUuid });
  return res.data;
}

// --- API Keys ---

export async function getApiKeys() {
  const res = await api.get('/api/apikeys');
  return res.data;
}

export async function createApiKey(name) {
  const res = await api.post('/api/apikeys', { name });
  return res.data;
}

export async function deleteApiKey(id) {
  const res = await api.delete(`/api/apikeys/${id}`);
  return res.data;
}

// --- Webhooks ---

export async function getWebhooks() {
  const res = await api.get('/api/webhooks');
  return res.data;
}

export async function createWebhook({ name, url, events }) {
  const res = await api.post('/api/webhooks', { name, url, events });
  return res.data;
}

export async function updateWebhook(id, data) {
  const res = await api.put(`/api/webhooks/${id}`, data);
  return res.data;
}

export async function deleteWebhook(id) {
  const res = await api.delete(`/api/webhooks/${id}`);
  return res.data;
}

// --- AI ---

export async function generateTasks(description, imageFile, detailLevel = 1) {
  const formData = new FormData();
  formData.append('description', description);
  formData.append('detailLevel', String(detailLevel));
  if (imageFile) {
    formData.append('image', imageFile);
  }
  const res = await api.post('/api/generateTask', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return res.data;
}
